"use client";

import { useState } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { useLanguage } from "@/context/LanguageContext";

export default function TestimonialsSection() {
  const { t } = useLanguage();

  const testimonials = [
    {
      id: "test_001",
      name: "Sarah Mitchell",
      location: "London, UK",
      rating: 5,
      textKey: "test_1_text" as const,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12632b18f-1763292960828.png",
      alt: "Smiling woman with brown hair in casual attire against white background",
      productKey: "test_1_product" as const,
    },
    {
      id: "test_002",
      name: "Marco Rossi",
      location: "Rome, Italy",
      rating: 5,
      textKey: "test_2_text" as const,
      image: "https://images.unsplash.com/photo-1599664786253-ff4632ff12a3",
      alt: "Middle-aged man with beard smiling in outdoor setting",
      productKey: "test_2_product" as const,
    },
    {
      id: "test_003",
      name: "Emily Chen",
      location: "San Francisco, USA",
      rating: 5,
      textKey: "test_3_text" as const,
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1940552c1-1763293876478.png",
      alt: "Young Asian woman with long dark hair smiling in professional headshot",
      productKey: "test_3_product" as const,
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-20 md:py-32 bg-secondary text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 moroccan-pattern"></div>
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 reveal will-animate">
          <span className="text-white/80 font-semibold text-sm uppercase tracking-wider">
            {t("test_badge")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            {t("test_title")}
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            {t("test_desc")}
          </p>
        </div>

        {/* Testimonials Slider */}
        <div className="max-w-4xl mx-auto reveal will-animate">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 glass-effect">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Customer Image */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white/20">
                  <AppImage
                    src={testimonials?.[activeIndex]?.image}
                    alt={testimonials?.[activeIndex]?.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="flex-1">
                {/* Stars */}
                <div className="flex gap-1 mb-4 justify-center md:justify-start">
                  {[...Array(testimonials?.[activeIndex]?.rating)]?.map((_, i) => (
                    <Icon
                      key={`star_${testimonials?.[activeIndex]?.id}_${i}`}
                      name="StarIcon"
                      size={24}
                      variant="solid"
                      className="text-warning"
                    />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-lg md:text-xl text-white leading-relaxed mb-6 text-center md:text-left">
                  "{t(testimonials?.[activeIndex]?.textKey)}"
                </p>

                {/* Customer Info */}
                <div className="text-center md:text-left">
                  <p className="font-bold text-white text-lg">
                    {testimonials?.[activeIndex]?.name}
                  </p>
                  <p className="text-white/70 text-sm">
                    {testimonials?.[activeIndex]?.location} • {t(testimonials?.[activeIndex]?.productKey)}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials?.map((_, index) => (
                <button
                  key={`dot_${index}`}
                  onClick={() => setActiveIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeIndex ? "bg-white w-8" : "bg-white/30 hover:bg-white/50"
                  }`}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 reveal will-animate">
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">500+</div>
            <div className="text-white/70 text-sm">{t("test_stat1")}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">4.9</div>
            <div className="text-white/70 text-sm">{t("test_stat2")}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">50+</div>
            <div className="text-white/70 text-sm">{t("test_stat3")}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl md:text-5xl font-bold text-white mb-2">25+</div>
            <div className="text-white/70 text-sm">{t("test_stat4")}</div>
          </div>
        </div>
      </div>
    </section>
  );
}